import {
  AnalyticsUtilsAbstract,
  SiteParams,
  IUacBackendInfo,
  UserInfo,
} from "@traceair/browser-tracking-lib";
import {
  getAuthState,
  getRouterState,
  getSiteState,
} from "../redux/rootReducer";
import { getPayloadFromToken, IUserProps } from "../redux/modules/auth/utils";
import storeInstance from "../redux/storeInstance";
import config from "../config";
import axios, { AxiosResponse } from "axios";
import pkg from "../../package.json";
import { ISite } from "../apiFacades/csu/site";

const { enabled, backendUrl, environment, location, subsystem } =
  config.tracking;
const appVersion = pkg.version;

interface ICustomer {
  id: string;
  name: string;
}

class GcpAnalyticsUtils extends AnalyticsUtilsAbstract {
  private static checkIfTaUser(user: IUserProps): boolean {
    return /@traceair\.net$/.test(user.email);
  }

  private isTaUser?: boolean;
  private userInfo?: UserInfo;

  constructor() {
    super();

    if (!enabled) {
      return;
    }
    this.setSessionId();
  }

  public updateUserInfo = async () => {
    const jwt = getAuthState(storeInstance.getState()).accessToken;
    if (!jwt) {
      return;
    }
    const { user } = getPayloadFromToken(jwt);
    const customers = await listCustomers();
    if (user) {
      this.isTaUser = GcpAnalyticsUtils.checkIfTaUser(user);
      this.userInfo = {
        userId: user.email,
        email: user.email,
        name: user.name || "",
        title: user.title || "",
        company: user.company ? user.company.name : "",
        department: user.department || "",
        companyType: user.company ? user.company.type : "",
        titleDescription: user.titleDescription || "",
        customers: customers.map((c) => c.name),
        phone: user.phone || "",
        lang: user.lang,
        lastname: user.lastname || "",
        createdTime: user.createdAt ? strDateToTimestamp(user.createdAt) : 0,
        updatedTime: user.updatedAt ? strDateToTimestamp(user.updatedAt) : 0,
        location,
      };
      if (this.isEnabled()) {
        this.setUserIdCookie(user.email);
        this.setLang(user.lang);
      }
    }
  };

  public getAppParams() {
    return {
      componentVersion: appVersion,
      component: "GCP App",
      componentData: {},
      subsystem,
      env: environment,
    };
  }

  public getSiteParams(): SiteParams {
    const routerState = getRouterState(storeInstance.getState()).data;
    const site: ISite = getSiteState(storeInstance.getState()).data;
    return {
      customer: routerState ? routerState.customerId : "",
      site: routerState ? routerState.siteId : "",
      siteName: site ? site.name : "",
    };
  }

  public getUserInfo(): UserInfo | undefined {
    return this.userInfo;
  }

  public async getBackendInfo(): Promise<IUacBackendInfo> {
    const jwt = getAuthState(storeInstance.getState()).accessToken;
    const { customer, site } = this.getSiteParams();
    if (jwt && site && customer) {
      const url = `${backendUrl}/api/customers/${customer}/sites/${site}/store-action`;
      return { url, jwt };
    } else {
      return {
        url: `${backendUrl}/api/store-action`,
      };
    }
  }

  public isEnabled() {
    return enabled && !this.isTaUser;
  }
}

function strDateToTimestamp(date: string): number {
  return new Date(date).getTime();
}

const CSUApi = axios.create({
  baseURL: config.endpoints.csuApi,
});

CSUApi.interceptors.request.use((axiosConfig) => {
  const jwt = getAuthState(storeInstance.getState()).accessToken;
  return {
    ...axiosConfig,
    headers: {
      Authorization: `Bearer ${jwt}`,
    },
  };
});

export const listCustomers = () => {
  return CSUApi.get(`customers`).then(
    (response: AxiosResponse<ICustomer[]>) => response.data,
  );
};

const analytics = new GcpAnalyticsUtils();

async function trackAction(eventMsg: string, params = {}) {
  await analytics.updateUserInfo();
  analytics.track(`${analytics.getAppParams().component} ${eventMsg}`, params);
}

export default trackAction;
